import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import ProductsMapList from "../ProductsMapList";
import CommonDrawer from "../drawer/CommonDrawer";
import axios from "axios";
import { apiBaseUrl } from "../../config";
import { BsGrid } from "react-icons/bs";
import { CiGrid2H } from "react-icons/ci";
import SingleOne from "../SingleOne";
import Loader from "../loader/Loader";
import { useParams } from "react-router-dom";
import { getDomainInfo } from "../../utils/utils";
import ProductFilter from "../ProductFilter/ProductFilter";

const AllProducts = ({ cartLength, categoryIds, onResetCategorySelection }) => {
  // const dispatch = useDispatch();
  const products = useSelector((state) => state.productslst?.products);
  const totalProductLength = useSelector((state) => state.productslst?.totalProducts);

  // Local state
  const [productList, setProductList] = useState([]);
  const [displayedCount, setDisplayedCount] = useState(12);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [skip, setSkip] = useState(12);
  const [isGridView, setIsGridView] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isFilterFullScreen, setFilterFullScreen] = useState(false);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});
  const [filterableProducts, setFilterableProducts] = useState([]);
  const [filterDataLoaded, setFilterDataLoaded] = useState(false);

  const { userId } = useParams();
  const { uid } = getDomainInfo(userId);
  // const storeId = localStorage.getItem("storeid");
  const customSortFromStore = useSelector((state) => state.fetchUser?.user?.customSort);
  const [sortState, setSortState] = useState(customSortFromStore || { createdAt: -1 });
  const userid = useSelector((state) => state.fetchUser?.user?.id);

  const observer = useRef(null);
  const lastItemRef = useRef();

  useEffect(() => {
    observer.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMoreItems();
        }
      },
      { threshold: 1 }
    );

    if (lastItemRef.current) {
      observer.current.observe(lastItemRef.current);
    }

    return () => {
      if (observer.current) observer.current.disconnect();
    };
  }, [productList]);

  // Load filter data on component mount
  useEffect(() => {
    const loadFilterData = async () => {
      if (!userid) {
        console.error("Cannot load filter data: userid is missing");
        return;
      }

      try {
        setIsLoading(true);
        const res = await axios.post(`${apiBaseUrl}user/filter-seller-products-by-category/${userid}`);
        if (res.data.success) {
          const enrichedProducts = res.data.products.map(p => ({
            ...p,
            price: p.price || 0,
            additionalInfo: {
              variantGroups: p.additionalInfo?.variantGroups || [],
              attributeGroups: p.additionalInfo?.attributeGroups || []
            }
          }));
          setFilterableProducts(enrichedProducts);
          setFilterDataLoaded(true);
        }
      } catch (err) {
        console.error("Failed to fetch variant/attribute filter data", err);
      } finally {
        setIsLoading(false);
      }
    };

    if (!filterDataLoaded && userid) {
      loadFilterData();
    }
  }, [userid, filterDataLoaded]);

  useEffect(() => {
    if (isFilterApplied) {
      setProductList(filteredProducts);
      setDisplayedCount(12);
    } else {
      setProductList(products);
      setDisplayedCount(12);
    }
  }, [products, filteredProducts, isFilterApplied]);

  const toggleGridView = () => {
    setIsGridView((prev) => !prev);
  };

  const loadMoreItems = () => {
    const totalItems = isFilterApplied ? filteredProducts.length : totalProductLength;

    if (displayedCount < totalItems) {
      if (isFilterApplied) {
        // If filter is applied, we just show more from the already-loaded filtered products
        setDisplayedCount((prev) => prev + 12);
      } else {
        // Only fetch more products if we have the necessary data
        if (!userid) {
          console.error("Cannot load more items: userid is missing");
          return;
        }

        setIsLoading(true);
        setTimeout(async () => {
          try {
            const response = await axios.post(
              `${apiBaseUrl}user/filter-seller-products-by-category/${userid}`,
              {
                categoryIds: categoryIds || [],
                customSort: sortState,
                skip: skip,
                limit: 12,
              }
            );

            if (response && response.data && response.data.products) {
              if (response.data.products.length > 0) {
                setProductList((prev) => [...prev, ...response.data.products]);
                setSkip((prev) => prev + 12);
                setDisplayedCount((prev) => prev + response.data.products.length);
              } else {
                // No more products to load
                console.log("No more products available");
              }
            }
          } catch (error) {
            console.error("Error fetching Products:", error);
          } finally {
            setIsLoading(false);
          }
        }, 1000);
      }
    }
  };

  const handleOpenDrawer = (product) => {
    setSelectedProduct(product);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedProduct(null);
  };

  const openFilterWithData = () => {
    if (filterDataLoaded && filterableProducts.length > 0) {
      setFilterFullScreen(true);
    } else if (!filterDataLoaded && userid) {
      // If filter data isn't loaded yet but we have userid, show loading while we fetch it
      setIsLoading(true);
      const loadFilterData = async () => {
        try {
          const res = await axios.post(`${apiBaseUrl}user/filter-seller-products-by-category/${userid}`);
          if (res.data.success) {
            const enrichedProducts = res.data.products.map(p => ({
              ...p,
              price: p.price || 0,
              additionalInfo: {
                variantGroups: p.additionalInfo?.variantGroups || [],
                attributeGroups: p.additionalInfo?.attributeGroups || []
              }
            }));
            setFilterableProducts(enrichedProducts);
            setFilterDataLoaded(true);
            setFilterFullScreen(true);
          }
        } catch (err) {
          console.error("Failed to fetch filter data", err);
        } finally {
          setIsLoading(false);
        }
      };
      loadFilterData();
    }
  };

  const displayedItems = isFilterApplied
    ? filteredProducts.slice(0, displayedCount)
    : productList.slice(0, displayedCount);

  const totalItems = isFilterApplied ? filteredProducts.length : totalProductLength;

  const handleFilterApply = ({ filteredProducts, filters }) => {
    setFilteredProducts(filteredProducts);
    setActiveFilters(filters);

    const isAnyFilterSet =
      filters.categories.length > 0 ||
      filters.subcategories.length > 0 ||
      filters.variants.length > 0 ||
      filters.attributes.length > 0 ||
      filters.sort;

    setIsFilterApplied(isAnyFilterSet);
    setDisplayedCount(12);
    setFilterFullScreen(false);

    if (onResetCategorySelection) {
      onResetCategorySelection();
    }
  };

  return (
    <>
      <div className="view-more-container">
        <div className="container">
          {isLoading && <Loader />}
          <div className="header-section">
            <h5 className="fontclass">
              {isFilterApplied ? filteredProducts.length : totalProductLength} products
            </h5>
            <div className="icons">
              <button onClick={toggleGridView} className="icon-button primary-font-color row-column-item-btn">
                {isGridView ? <BsGrid /> : <CiGrid2H />}
              </button>
              <span>|</span>
              <button className="icon-button primary-font-color filter-btn" onClick={openFilterWithData}>
                {/* <img src="/Images/Icons/filter.png" alt="filter" /> */}
                <svg width="20" height="16" viewBox="0 0 20 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.6562 0.5C13.2422 0.5 13.75 1.00781 13.75 1.59375C13.75 1.86719 13.6328 2.10156 13.4766 2.29688L9.375 6.98438V14.5625C9.375 15.1094 8.94531 15.5 8.39844 15.5C8.20312 15.5 8.00781 15.4609 7.8125 15.3438L4.84375 13.0781C4.53125 12.8438 4.375 12.4922 4.375 12.0625V6.98438L0.234375 2.29688C0.078125 2.10156 0 1.86719 0 1.59375C0 1.00781 0.46875 0.5 1.05469 0.5H12.6562ZM5.625 6.75V12.0625L8.125 13.9766V6.75C8.125 6.63281 8.16406 6.47656 8.24219 6.35938L12.2656 1.75H1.44531L5.46875 6.35938C5.54688 6.47656 5.58594 6.63281 5.58594 6.75H5.625ZM19.375 13.625C19.6875 13.625 20 13.9375 20 14.25C20 14.6016 19.6875 14.875 19.375 14.875H13.125C12.7734 14.875 12.5 14.6016 12.5 14.25C12.5 13.9375 12.7734 13.625 13.125 13.625H19.375ZM12.5 8C12.5 7.6875 12.7734 7.375 13.125 7.375H19.375C19.6875 7.375 20 7.6875 20 8C20 8.35156 19.6875 8.625 19.375 8.625H13.125C12.7734 8.625 12.5 8.35156 12.5 8ZM19.375 1.125C19.6875 1.125 20 1.4375 20 1.75C20 2.10156 19.6875 2.375 19.375 2.375H15.625C15.2734 2.375 15 2.10156 15 1.75C15 1.4375 15.2734 1.125 15.625 1.125H19.375Z" fill="currentcolor" /></svg>
              </button>
            </div>
          </div>

          <div className="d-flex justify-content-between ProductItemContainer" style={{ paddingBottom: "62px" }}>
            {isFilterApplied ? (
              filteredProducts.length > 0 ? (
                isGridView ? (
                  <ProductsMapList productList={displayedItems} handleOpenDrawer={handleOpenDrawer} />
                ) : (
                  <SingleOne productList={displayedItems} handleOpenDrawer={handleOpenDrawer} />
                )
              ) : (
                <p className="text-center d-block w-100">No products match your filters</p>
              )
            ) : productList?.length > 0 ? (
              isGridView ? (
                <ProductsMapList productList={displayedItems} handleOpenDrawer={handleOpenDrawer} />
              ) : (
                <SingleOne productList={displayedItems} handleOpenDrawer={handleOpenDrawer} />
              )
            ) : (
              <p className="text-center d-block w-100">No Record Found!</p>
            )}
          </div>

          {displayedCount < totalItems ? (
            <p onClick={loadMoreItems} className="text-center button theme-btn fontclass primary-font-color">View more...</p>
          ) : (
            <p className="text-center fontclass primary-font-color">
              All Products Fetched!
            </p>
          )}

          <CommonDrawer isOpen={isDrawerOpen} onClose={handleCloseDrawer} product={selectedProduct} />
        </div>
        </div>

        {isFilterFullScreen && filterDataLoaded && (
          <ProductFilter
            products={filterableProducts}
            initialFilters={activeFilters}
            onApply={handleFilterApply}
            onClose={() => setFilterFullScreen(false)}
          />
        )}
      </>
      );
};

      export default AllProducts;